/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type APIPermissions,
  AppSystemPermissions,
  NewFeaturePermissions,
  NotificationSeverity,
  ProjectsAPIPermissions,
  ScenarioAPIPermissions,
  type SystemIssueAlert,
  type SystemIssueAlertSeverity,
  systemIssueAlertSeverities,
  ToolAPIPermissions,
} from '@poweralpha/common';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { z } from 'zod';
import { queryClient } from '../queryClient';
import { formHook, getStandardValidators } from '../react-components/forms/form';
import { InfoPill } from '../react-components/text';
import { usePageTitle } from '../react-components/usePageTitle';
import {
  createSystemIssueAlert,
  resolveSystemIssueAlert,
  systemIssueAlertsQueryKey,
  updateSystemIssueAlert,
  useSystemIssueAlertsQuery,
} from './api';

interface FormState {
  message: string;
  severity: SystemIssueAlertSeverity;
  permissions: Array<APIPermissions>;
  started_at: Date | null;
}

const systemIssueAlertFormSchema = z.object({
  message: z.string().trim().min(1, 'Message is required'),
  severity: z.enum(systemIssueAlertSeverities),
  permissions: z.array(z.custom<APIPermissions>((permission) => typeof permission === 'string')),
  started_at: z.date().nullable().refine(Boolean, 'Started is required'),
});

const permissionOptions: Array<APIPermissions> = [
  ...Object.values(ToolAPIPermissions),
  ...Object.values(ScenarioAPIPermissions),
  ...Object.values(ProjectsAPIPermissions),
  ...Object.values(AppSystemPermissions),
  ...Object.values(NewFeaturePermissions),
];

const defaultFormState = (): FormState => ({
  message: '',
  severity: NotificationSeverity.INFO,
  permissions: [],
  started_at: new Date(),
});

const formatDate = (date: Date | null) =>
  date ? new Date(date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

const SystemIssuePermissionPills = ({ permissions }: { permissions: Array<APIPermissions> }) => {
  if (permissions.length === 0) {
    return <InfoPill label="All users" height={18} color="info" />;
  }

  return (
    <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {permissions.map((permission) => (
        <InfoPill key={permission} label={permission} height={18} />
      ))}
    </Stack>
  );
};

const SystemIssueSeverityIcon = ({ severity }: { severity: SystemIssueAlertSeverity }) => {
  switch (severity) {
    case NotificationSeverity.ERROR:
      return <ErrorOutlineIcon color={severity} fontSize="small" />;
    case NotificationSeverity.WARNING:
      return <WarningAmberIcon color={severity} fontSize="small" />;
    default:
      return <InfoOutlinedIcon color={severity} fontSize="small" />;
  }
};

const invalidateSystemIssues = async () => {
  await queryClient.invalidateQueries({ queryKey: systemIssueAlertsQueryKey });
};

const SystemIssueAdmin = () => {
  usePageTitle('PowerAlpha - System Alerts');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<SystemIssueAlert | null>(null);

  const query = useSystemIssueAlertsQuery();

  const createMutation = useMutation({
    mutationFn: createSystemIssueAlert,
    onSuccess: async () => {
      setDialogOpen(false);
      await invalidateSystemIssues();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: FormState }) =>
      updateSystemIssueAlert(id, {
        message: payload.message,
        severity: payload.severity,
        permissions: payload.permissions,
        started_at: payload.started_at?.toISOString(),
      }),
    onSuccess: async () => {
      setDialogOpen(false);
      await invalidateSystemIssues();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: resolveSystemIssueAlert,
    onSuccess: invalidateSystemIssues,
  });

  const rows = useMemo(() => query.data?.rows ?? [], [query.data?.rows]);

  const appForm = formHook.useAppForm({
    defaultValues: defaultFormState(),
    validators: getStandardValidators(systemIssueAlertFormSchema),
    onSubmit: ({ value }) => {
      const parsed = systemIssueAlertFormSchema.parse(value);
      if (!parsed.started_at) return;

      const payload = { ...parsed, started_at: parsed.started_at };
      if (editingAlert) {
        updateMutation.mutate({ id: editingAlert.id, payload });
        return;
      }

      createMutation.mutate({ ...payload, started_at: payload.started_at.toISOString() });
    },
  });

  const openCreateDialog = () => {
    setEditingAlert(null);
    setDialogOpen(true);
  };

  const openEditDialog = (alert: SystemIssueAlert) => {
    setEditingAlert(alert);
    setDialogOpen(true);
  };

  const resetForm = useEffectEvent((values: FormState) => {
    appForm.reset(values);
    appForm.validate('mount');
  });
  useEffect(() => {
    if (!dialogOpen) return;

    resetForm(
      editingAlert
        ? {
            message: editingAlert.message,
            severity: editingAlert.severity,
            permissions: editingAlert.permissions ?? [],
            started_at: new Date(editingAlert.started_at),
          }
        : defaultFormState(),
    );
  }, [dialogOpen, editingAlert]);

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Container
      maxWidth="lg"
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        pt: 1,
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}
        >
          <Typography variant="h5">System Alerts</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New Alert
          </Button>
        </Stack>
        {query.error && <Alert severity="error">{query.error.message}</Alert>}
        <TableContainer
          data-testid="system-issues-table-container"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            '& .MuiTableCell-stickyHeader': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Resolved</TableCell>
                <TableCell>Modified By</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell>
                    <Chip
                      size="small"
                      color={alert.resolved_at ? 'default' : 'success'}
                      label={alert.resolved_at ? 'Resolved' : 'Active'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={alert.severity}>
                      <span role="img" aria-label={`${alert.severity} severity`}>
                        <SystemIssueSeverityIcon severity={alert.severity} />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{alert.message}</TableCell>
                  <TableCell>
                    <SystemIssuePermissionPills permissions={alert.permissions ?? []} />
                  </TableCell>
                  <TableCell>{formatDate(alert.started_at)}</TableCell>
                  <TableCell>{formatDate(alert.resolved_at)}</TableCell>
                  <TableCell>{alert.modified_by}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit alert">
                      <span>
                        <IconButton
                          aria-label="Edit alert"
                          size="small"
                          disabled={Boolean(alert.resolved_at)}
                          onClick={() => openEditDialog(alert)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Resolve alert">
                      <span>
                        <IconButton
                          aria-label="Resolve alert"
                          size="small"
                          disabled={Boolean(alert.resolved_at) || resolveMutation.isPending}
                          onClick={() => resolveMutation.mutate(alert.id)}
                        >
                          <TaskAltIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingAlert ? 'Edit Alert' : 'New Alert'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <appForm.AppField
              name="message"
              children={(field) => <field.FormTextArea label="Message" minRows={3} maxRows={8} />}
            />
            <appForm.AppField
              name="severity"
              children={(field) => (
                <field.FormAutocomplete
                  label="Severity"
                  freeSolo={false}
                  options={systemIssueAlertSeverities}
                  disableClearable
                />
              )}
            />
            <appForm.AppField
              name="started_at"
              children={(field) => <field.FormDateField label="Started" mode="dateTime" />}
            />
            <appForm.AppField
              name="permissions"
              children={(field) => (
                <field.FormAutocomplete
                  label="Permissions"
                  multiple
                  freeSolo={false}
                  options={permissionOptions}
                  noOptionsText="No permissions"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <appForm.AppForm>
            <appForm.SubmitButton loading={saving} disabled={saving}>
              Save
            </appForm.SubmitButton>
          </appForm.AppForm>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SystemIssueAdmin;
